import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Loader2, Plus, Shield } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { P, PermissionGuard } from '@/features/permissions';
import { useRoles, useCreateRole } from '../api/roles-api';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

function CreateRoleDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createRole = useCreateRole();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await createRole.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success('Role created');
      setName('');
      setDescription('');
      setOpen(false);
    } catch {
      toast.error('Failed to create role');
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Role
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Role</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              required
              value={name}
              placeholder="Role name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="role-desc">Description</Label>
            <Textarea
              id="role-desc"
              rows={2}
              value={description}
              placeholder="Optional description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createRole.isPending}>
              {createRole.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RolesPage() {
  return (
    <PermissionGuard permission={P.MANAGE_USERS}>
      <RolesPageContent />
    </PermissionGuard>
  );
}

function RolesPageContent() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const { formatInstant } = useOrgDateFormat();
  const { isLoading, data: roles } = useRoles();

  useEffect(() => {
    setPageHeader({
      title: 'Roles',
      description: 'Manage organization roles and permissions',
      actions: <CreateRoleDialog />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rolesList = roles ?? [];

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-category-plum-surface text-category-plum">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Roles</p>
                <p className="text-2xl font-bold">{rolesList.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rolesList.map((role) => (
              <TableRow
                key={role.id}
                className="cursor-pointer"
                onClick={() => navigate({ to: '/roles/$roleId', params: { roleId: role.id } })}>
                <TableCell className="font-medium">{role.name}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  {role.description ?? '—'}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  {formatInstant(role.created_at, 'MMM d, yyyy')}
                </TableCell>
              </TableRow>
            ))}
            {rolesList.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                  No roles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
